<template>
  <div class="settings-toggle-table">
    <label v-if="label" class="stt-label">
      {{ label }}
      <vue-feather v-if="tooltip" type="info" size="14" class="stt-label-tooltip" v-tooltip="tooltip" />
    </label>
    <div class="stt-table-wrap">
      <table class="stt-table">
        <thead>
          <tr>
            <th v-for="group in groups" :key="group.label" class="stt-col-head">{{ group.label }}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td v-for="group in groups" :key="group.label" class="stt-col">
              <div v-for="item in group.items" :key="item.key" class="stt-item">
                <span class="stt-name">
                  {{ item.label }}
                  <vue-feather v-if="item.description" type="info" size="12" class="stt-info"
                    v-tooltip="item.description" />
                </span>
                <ToggleSwitch :modelValue="!!modelValue[item.key]" :disabled="disabled"
                  @update:modelValue="(v) => onToggle(item.key, v)" />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
// Generalizable settings table: renders one column per group, each listing labeled boolean
// settings with a toggle. v-model binds an object map of { key: boolean }. Per-item
// descriptions surface as hover tooltips. Reusable for any on/off settings matrix.
import ToggleSwitch from './ToggleSwitch.vue'
import VueFeather from 'vue-feather'

export default {
  name: 'SettingsToggleTable',
  components: { ToggleSwitch, 'vue-feather': VueFeather },
  props: {
    // Object map of { [key]: boolean }
    modelValue: {
      type: Object,
      required: true
    },
    // One column per entry: [{ label, items: [{ key, label, description? }] }]
    groups: {
      type: Array,
      default: () => []
    },
    disabled: {
      type: Boolean,
      default: false
    },
    label: {
      type: String,
      default: ''
    },
    tooltip: {
      type: String,
      default: ''
    }
  },
  emits: ['update:modelValue', 'change'],
  created() {
    if (typeof this.modelValue !== 'object' || this.modelValue === null) {
      throw new Error('SettingsToggleTable: modelValue must be an object map of { key: boolean }')
    }
  },
  methods: {
    onToggle(key, value) {
      this.$emit('update:modelValue', { ...this.modelValue, [key]: value })
      this.$emit('change', key, value)
    }
  }
}
</script>

<style scoped>
.settings-toggle-table {
  display: flex;
  flex-direction: column;
}

.stt-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.stt-label-tooltip {
  color: #9ca3af;
  cursor: help;
  flex-shrink: 0;
}

.stt-table-wrap {
  border: 1px solid #d1d5db;
  border-radius: 4px;
  overflow: hidden;
  background: #fff;
}

.stt-table {
  width: 100%;
  border-collapse: collapse;
}

.stt-col-head {
  text-align: left;
  vertical-align: bottom;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b7280;
  padding: 10px 16px;
  min-width: 150px;
  background: #f9fafb;
  border-bottom: 1px solid #d1d5db;
}

.stt-col {
  vertical-align: top;
  padding: 0 16px;
}

/* vertical dividers between category columns */
.stt-col-head:not(:last-child),
.stt-col:not(:last-child) {
  border-right: 1px solid #e5e7eb;
}

/* horizontal lines separating each button row within a column */
.stt-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 11px 0;
  border-bottom: 1px solid #e5e7eb;
}

.stt-item:last-child {
  border-bottom: none;
}

.stt-name {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.9375rem;
  color: #1f2937;
}

.stt-info {
  color: #9ca3af;
  cursor: help;
  flex-shrink: 0;
}
</style>
